import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { Alerta, LoteAncorado, Pessoa, Unidade } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PAPEIS_DE_CAMPO, PAPEIS_DE_CONSOLE, nomeDoPapel } from '../lib/format/roles';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';
import { emDataHora, hashCurto } from '../lib/format/datetime';

/* ------------------------------------------------------------------ unidades (A10) */

/**
 * As unidades da cadeia: fábrica, centro de distribuição, loja, assistência.
 *
 * <p>Criar unidade <b>já cria o vínculo</b> com a empresa (ADR-046). Não há tela de gestão
 * de vínculo, e a ausência é a decisão: no MVP a cadeia é interna, e uma tela de vínculo
 * sugeriria um modelo de parceria que o produto ainda não tem.
 */
export function UnidadesPage() {
  const clienteDeConsulta = useQueryClient();
  const [razao, setRazao] = useState('');
  const [tipo, setTipo] = useState('FACTORY');

  const unidades = useQuery({
    queryKey: ['unidades'],
    queryFn: () => api.get<Unidade[]>('/console/units'),
  });
  const criar = useMutation({
    mutationFn: () => api.post<Unidade>('/console/units', { legalName: razao, actorType: tipo }),
    onSuccess: () => {
      setRazao('');
      void clienteDeConsulta.invalidateQueries({ queryKey: ['unidades'] });
    },
  });

  // O nome curto das telas do aplicativo (ADR-129). A razão social não se edita: o passaporte a
  // mostra nas etapas antigas, e mudá-la reescreveria o que já foi publicado.
  const [editando, setEditando] = useState<string | null>(null);
  const [nomeCurto, setNomeCurto] = useState('');
  const nomear = useMutation({
    mutationFn: (unitId: string) =>
      api.put<Unidade>(`/console/units/${unitId}/short-name`, { shortName: nomeCurto }),
    onSuccess: () => {
      setEditando(null);
      void clienteDeConsulta.invalidateQueries({ queryKey: ['unidades'] });
    },
  });

  return (
    <>
      <PageHeader
        titulo="Unidades"
        subtitulo="Os lugares da sua cadeia. É a uma unidade que o operador se autentica, e não à empresa."
      />
      <div className="wrap">
        <div className="duas">
          <div className="card tbl">
            {unidades.isPending && <Carregando o="as unidades" />}
            {unidades.isError && <Erro erro={unidades.error} />}
            {unidades.data?.length === 0 && <Vazio titulo="Nenhuma unidade" />}
            {unidades.data && unidades.data.length > 0 && (
              <table className="t">
                <thead>
                  <tr>
                    <th>Unidade</th>
                    <th>Nome no aplicativo</th>
                    <th>Tipo</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {unidades.data.map((u) => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 600 }}>{u.legalName}</td>
                      <td>
                        {editando === u.id ? (
                          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input
                              value={nomeCurto}
                              maxLength={40}
                              placeholder="CD Campinas"
                              onChange={(e) => setNomeCurto(e.target.value)}
                              style={{ width: 160 }}
                            />
                            <button
                              className="btn btn-primary"
                              disabled={nomear.isPending}
                              onClick={() => nomear.mutate(u.id)}
                            >
                              Salvar
                            </button>
                            <button className="btn btn-ghost" onClick={() => setEditando(null)}>
                              Cancelar
                            </button>
                          </span>
                        ) : (
                          <span>
                            {u.shortName ?? <span className="t-cap">usa a razão social</span>}{' '}
                            <button
                              className="btn btn-ghost"
                              onClick={() => {
                                setEditando(u.id);
                                setNomeCurto(u.shortName ?? '');
                              }}
                            >
                              {u.shortName ? 'Alterar' : 'Dar nome curto'}
                            </button>
                          </span>
                        )}
                      </td>
                      <td>{u.actorType}</td>
                      <td>
                        <span className="chip chip-neutral">{u.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {nomear.isError && <Erro erro={nomear.error} />}
            <p className="dica" style={{ padding: '0 16px 12px' }}>
              O nome curto aparece no alto das telas do aplicativo. O passaporte e o certificado
              continuam com a razão social.
            </p>
          </div>

          <div className="card cd">
            <div className="cd2-t">Nova unidade</div>
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                criar.mutate();
              }}
            >
              <div className="campo">
                <label htmlFor="r">Razão social</label>
                <input id="r" value={razao} onChange={(e) => setRazao(e.target.value)} required />
              </div>
              <div className="campo">
                <label htmlFor="t">Tipo</label>
                <select id="t" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="FACTORY">Fábrica</option>
                  <option value="DC">Centro de distribuição</option>
                  <option value="CARRIER">Transportadora</option>
                  <option value="RETAIL">Loja</option>
                  <option value="SERVICE">Assistência</option>
                </select>
              </div>
              {criar.isError && <div className="aviso ruim">{String(criar.error)}</div>}
              <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                Criar unidade
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- pessoas (A12) */

/**
 * Pessoas e papéis (A12), com a matriz do ADR-041 <b>visível na tela</b>.
 *
 * <p>Sete papéis acumuláveis. Quem concede precisa ver o que cada um abre no momento de
 * conceder — sem isso, o caminho de menor resistência é dar administrador para todo mundo, e
 * a separação entre catálogo, provisionamento e segurança deixa de existir na prática.
 */
export function PessoasPage() {
  const clienteDeConsulta = useQueryClient();
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [papeis, setPapeis] = useState<string[]>(['COMPANY_CATALOG']);
  const [unitId, setUnitId] = useState('');

  const pessoas = useQuery({
    queryKey: ['pessoas'],
    queryFn: () => api.get<Pessoa[]>('/console/users'),
  });
  const unidades = useQuery({
    queryKey: ['unidades'],
    queryFn: () => api.get<Unidade[]>('/console/units'),
  });

  const convidar = useMutation({
    mutationFn: () =>
      api.post<Pessoa>('/console/users', {
        email,
        fullName: nome,
        password: senha,
        roles: papeis,
        unitId: unitId || null,
      }),
    onSuccess: () => {
      setEmail('');
      setNome('');
      setSenha('');
      void clienteDeConsulta.invalidateQueries({ queryKey: ['pessoas'] });
    },
  });

  const precisaDeUnidade = papeis.some((p) => p.startsWith('ACTOR_'));

  return (
    <>
      <PageHeader
        titulo="Pessoas e papéis"
        subtitulo="Os papéis são acumuláveis, e cada um abre uma porta diferente."
      />
      <div className="wrap">
        <div className="duas">
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="card tbl">
              {pessoas.isPending && <Carregando o="as pessoas" />}
              {pessoas.isError && <Erro erro={pessoas.error} />}
              {pessoas.data && pessoas.data.length > 0 && (
                <table className="t">
                  <thead>
                    <tr>
                      <th>Vínculo</th>
                      <th>Papéis</th>
                      <th>Unidade</th>
                      <th>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pessoas.data.map((p) => (
                      <tr key={p.id}>
                        <td className="mono-s">{p.userId.slice(0, 8)}…</td>
                        <td>
                          {p.roles.map((r) => (
                            <span key={r} className="chip chip-navy" style={{ marginRight: 4 }}>
                              {nomeDoPapel(r)}
                            </span>
                          ))}
                        </td>
                        <td className="t-cap">{p.actorId ? p.actorId.slice(0, 8) + '…' : '—'}</td>
                        <td>
                          <span className="chip chip-neutral">{p.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="card cd">
              <div className="cd2-t">O que cada papel abre</div>
              <table className="t">
                <tbody>
                  {[...PAPEIS_DE_CONSOLE, ...PAPEIS_DE_CAMPO].map((p) => (
                    <tr key={p.codigo}>
                      <td style={{ fontWeight: 600, width: 190 }}>
                        {p.nome}
                        {p.perigoso && (
                          <span className="chip chip-warn" style={{ marginLeft: 6 }}>
                            sensível
                          </span>
                        )}
                      </td>
                      <td className="t-small">{p.permite}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card cd">
            <div className="cd2-t">Convidar</div>
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                convidar.mutate();
              }}
            >
              <div className="campo">
                <label htmlFor="e">E-mail</label>
                <input id="e" type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} required />
              </div>
              <div className="campo">
                <label htmlFor="n">Nome</label>
                <input id="n" value={nome} onChange={(ev) => setNome(ev.target.value)} required />
              </div>
              <div className="campo">
                <label htmlFor="s">Senha inicial</label>
                <input id="s" value={senha} onChange={(ev) => setSenha(ev.target.value)} required />
                <span className="dica">
                  Hoje o administrador define a senha. Convite por e-mail e redefinição ainda
                  não existem no servidor.
                </span>
              </div>
              <div className="campo">
                <label>Papéis</label>
                {[...PAPEIS_DE_CONSOLE, ...PAPEIS_DE_CAMPO].map((p) => (
                  <label key={p.codigo} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={papeis.includes(p.codigo)}
                      onChange={(ev) =>
                        setPapeis((atual) =>
                          ev.target.checked
                            ? [...atual, p.codigo]
                            : atual.filter((c) => c !== p.codigo),
                        )
                      }
                    />
                    {p.nome}
                  </label>
                ))}
              </div>
              {precisaDeUnidade && (
                <div className="campo">
                  <label htmlFor="u">Unidade</label>
                  <select id="u" value={unitId} onChange={(ev) => setUnitId(ev.target.value)} required>
                    <option value="">Escolha…</option>
                    {(unidades.data ?? []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.legalName}
                      </option>
                    ))}
                  </select>
                  <span className="dica">
                    Papel de campo se autentica em uma unidade, não na empresa (ADR-017).
                  </span>
                </div>
              )}
              {convidar.isError && <div className="aviso ruim">{String(convidar.error)}</div>}
              <button className="btn btn-primary" type="submit" disabled={convidar.isPending}>
                Convidar
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- alertas (A18) */

export function AlertasPage() {
  const { traduzir } = useAuth();
  const [incluirResolvidos, setIncluir] = useState(false);
  const alertas = useQuery({
    queryKey: ['alertas', incluirResolvidos],
    queryFn: () => api.get<Alerta[]>(`/console/alerts?includeResolved=${incluirResolvidos}`),
  });

  return (
    <>
      <PageHeader
        titulo="Alertas"
        subtitulo="O que o servidor percebeu e o que os operadores relataram — e ninguém ainda olhou."
        acoes={
          <button className="btn btn-secondary" onClick={() => setIncluir((v) => !v)}>
            {incluirResolvidos ? 'Só os abertos' : 'Incluir resolvidos'}
          </button>
        }
      />
      <div className="wrap">
        {alertas.isPending && <Carregando o="os alertas" />}
        {alertas.isError && <Erro erro={alertas.error} />}
        {alertas.data && alertas.data.length === 0 && (
          <Vazio
            titulo="Nada na fila"
            detalhe="Nenhuma etiqueta recusada, nenhuma leitura implausível, nenhum item marcado lido."
          />
        )}
        {alertas.data && alertas.data.length > 0 && (
          <div className="card tbl">
            <table className="t">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Tipo</th>
                  <th>Item</th>
                  <th>Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {alertas.data.map((a) => (
                  <tr key={a.id}>
                    <td className="t-cap">{emDataHora(a.quando)}</td>
                    <td>
                      <span className={a.resolvido ? 'chip chip-neutral' : 'chip chip-danger'}>
                        {traduzir('alert', a.tipo)}
                      </span>
                    </td>
                    {/* A série leva ao item, e é lá que se marca. Sem o link, a fila dizia
                        qual peça e deixava a pessoa procurá-la à mão na busca. */}
                    <td className="mono-s">
                      {a.itemId && a.serial ? (
                        <Link to={`/itens/${a.itemId}`}>{a.serial}</Link>
                      ) : (
                        a.serial ?? '—'
                      )}
                    </td>
                    <td className="t-small">{detalheDoAlerta(a, traduzir)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ----------------------------------------------------------------- ancoragem */

/**
 * Os lotes ancorados.
 *
 * <p>Responde à pergunta que o cliente faz depois de fechar um turno — "e aí, foi para a
 * rede?" — sem pedir que ele acredite na resposta: a raiz está ali, e a rede também. Uma
 * coluna diz <b>publicada</b>, e enquanto ela disser "não", o resto da tela não usa a
 * palavra "ancorado".
 */
export function AncoragemPage() {
  const lotes = useQuery({
    queryKey: ['ancoragem'],
    queryFn: () => api.get<LoteAncorado[]>('/console/anchors'),
  });

  return (
    <>
      <PageHeader
        titulo="Ancoragem"
        subtitulo="Cada lote de eventos resumido numa raiz criptográfica, e onde ela foi parar."
      />
      <div className="wrap">
        {lotes.isPending && <Carregando o="os lotes" />}
        {lotes.isError && <Erro erro={lotes.error} />}
        {lotes.data && lotes.data.length === 0 && (
          <Vazio
            titulo="Nenhum lote ainda"
            detalhe="Um lote nasce quando uma sessão fecha, ou quando a varredura recolhe eventos avulsos."
          />
        )}
        {lotes.data && lotes.data.length > 0 && (
          <>
            {lotes.data.some((l) => !l.publicada) && (
              <div className="aviso" style={{ marginBottom: 16 }}>
                Neste piloto a publicação é <strong>simulada</strong>: a raiz é calculada de
                verdade e o lote é fechado de verdade, mas nada foi enviado a rede externa
                nenhuma. A conferência independente ainda não é possível.
              </div>
            )}
            <div className="card tbl">
              <table className="t">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Tipo</th>
                    <th>Folhas</th>
                    <th>Raiz</th>
                    <th>Rede</th>
                    <th>Transação</th>
                  </tr>
                </thead>
                <tbody>
                  {lotes.data.map((l) => (
                    <tr key={l.id}>
                      <td className="t-cap">{emDataHora(l.quando)}</td>
                      <td>{l.tipo === 'SESSION' ? 'Sessão' : 'Avulsos'}</td>
                      <td className="num">{l.folhas}</td>
                      <td className="mono-s" title={l.raiz}>
                        {hashCurto(l.raiz, 8)}
                      </td>
                      <td>
                        <span className={l.publicada ? 'chip chip-teal' : 'chip chip-warn'}>
                          {l.publicada ? l.rede : 'simulada'}
                        </span>
                      </td>
                      <td className="mono-s">{l.txHash ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------------- conta (A4) */

export function ContaPage() {
  const { sessao } = useAuth();
  if (!sessao) {
    return <Carregando />;
  }
  return (
    <>
      <PageHeader titulo="Conta" subtitulo="Quem você é, e em qual empresa está trabalhando." />
      <div className="wrap" style={{ maxWidth: 560 }}>
        <div className="card cd">
          <div className="kv">
            <span className="k">Nome</span>
            <span className="v">{sessao.nome}</span>
          </div>
          <div className="kv">
            <span className="k">E-mail</span>
            <span className="v">{sessao.email}</span>
          </div>
          <div className="kv">
            <span className="k">Empresa</span>
            <span className="v">{sessao.nomeFantasia ?? sessao.razaoSocial}</span>
          </div>
          <div className="kv">
            <span className="k">Papéis</span>
            <span className="v">{sessao.papeis.map(nomeDoPapel).join(', ')}</span>
          </div>
          {sessao.brandId && (
            <div className="kv">
              <span className="k">Marca</span>
              <span className="v">recorte de uma marca do grupo</span>
            </div>
          )}
        </div>
        <div className="t-cap" style={{ marginTop: 14 }}>
          Trocar a senha e redefinir por e-mail ainda não existem no servidor. Peça ao
          administrador da sua empresa.
        </div>
      </div>
    </>
  );
}

/**
 * O detalhe de um alerta, em frase.
 *
 * <p>Até aqui a coluna mostrava o JSON gravado — `{"marcacao":"STOLEN","bizStep":"receiving"}` —
 * para quem trata carga roubada às duas da manhã. Cada tipo guarda chaves diferentes, e é aqui,
 * num lugar só, que elas viram texto; o que não se reconhece cai no texto cru, mas nunca some.
 */
function detalheDoAlerta(
  a: Alerta,
  traduzir: (familia: string, valor: string | null | undefined) => string,
): string {
  let d: Record<string, string | undefined>;
  try {
    d = JSON.parse(a.detalhe || '{}');
  } catch {
    return a.detalhe;
  }
  switch (a.tipo) {
    case 'OPERATOR_REPORT': {
      const onde = d.elo ? `relatado por ${d.elo}` : 'relatado no aparelho';
      const nota = d.observacao ? `: “${d.observacao}”` : '';
      return `${traduzir('reportReason', d.motivo)} — ${onde}${nota}`;
    }
    case 'BLOCKED_ITEM_READ': {
      const regiao = d.geohash && d.geohash !== 'null' ? ` · região ${d.geohash}` : '';
      return `Marcado como ${traduzir('marking', d.marcacao).toLowerCase()}, e lido em ${traduzir('bizStep', d.bizStep).toLowerCase()}${regiao}`;
    }
    case 'GEO_IMPLAUSIBLE':
    case 'ITEM_FIELD_MISSING':
    case 'ITEM_FIELD_UNDECLARED':
    case 'ITEM_FIELD_INVALID':
    case 'OUT_OF_TERRITORY':
      return d.descricao ?? '—';
    case 'SUSPECTED_CLONE':
    case 'INVALID_CMAC':
    case 'COUNTER_REGRESSION':
    case 'UNKNOWN_TAG':
    case 'TAG_MISMATCH':
    case 'DISABLED_TAG_READ':
    case 'NFC_KEY_UNAVAILABLE':
    case 'TAG_TAMPER_OPENED':
      return d.reason ?? '—';
    default:
      return Object.keys(d).length === 0 ? '—' : a.detalhe;
  }
}
